/**
 * Variant media grouping for the product gallery.
 *
 * The gallery is ordered on purpose: per variant first that variant's packshot,
 * then a mood shot, then the remaining photos of the same colour, and only then
 * the next variant. Media linked to a variant are marked with
 * data-variant-anchor in the markup, so a variant's block of photos runs from
 * its own anchor up to (but not including) the next anchor. Photos before the
 * first anchor belong to no variant and stay hidden; they serve as the
 * collection image.
 *
 * Selecting a variant shrinks the main slider, the thumbnails and the zoom
 * popup to that block, in the authored order, starting at the packshot. All
 * three lists are filtered identically so Swiper's index-based syncing between
 * them keeps working.
 *
 * Slides are detached rather than hidden with CSS: Swiper keeps counting slides
 * that are still in the wrapper, which breaks navigation and pagination.
 */
(function () {
  const galleries = [];

  const mediaIdOf = (el) =>
    el.dataset.mediaId || el.dataset.mediaSubId || el.dataset.mediaModalId || null;

  const positionOf = (el) => Number(el.dataset.mediaPosition) || 0;

  const isGalleryItem = (el) => el.hasAttribute("data-media-position");

  class VariantMediaGroup {
    constructor(section, sectionId) {
      this.section = section;
      this.sectionId = sectionId || section.dataset.section;
      this.lists = [];
      this.collectLists();
    }

    /**
     * Every list (main slider, thumbnails, zoom popup) renders the same media
     * set. Snapshot each of them once, sorted by the authored position: the
     * selected variant's featured media is hoisted to the front when the
     * section renders, so DOM order is not the order the merchant set up.
     */
    collectLists() {
      const wrappers = new Set();

      this.section
        .querySelectorAll("[data-media-position]")
        .forEach((item) => wrappers.add(item.parentElement));

      wrappers.forEach((wrapper) => {
        const items = Array.from(wrapper.children)
          .filter(isGalleryItem)
          .sort((a, b) => positionOf(a) - positionOf(b));

        if (!items.length) return;

        this.lists.push({
          wrapper,
          items,
          swiperEl: wrapper.closest(".swiper"),
        });
      });

      // Any list holds the full media set; use the longest one as reference.
      this.reference = this.lists.reduce(
        (longest, list) =>
          !longest || list.items.length > longest.items.length ? list : longest,
        null
      );
    }

    /**
     * Media ids to keep for the given anchor, or null to show everything
     * (variant without linked media, or a media id that is not in the gallery).
     */
    rangeFor(targetMediaId) {
      if (!targetMediaId || !this.reference) return null;

      const items = this.reference.items;
      const start = items.findIndex((el) => mediaIdOf(el) === targetMediaId);

      if (start === -1) return null;

      let end = start + 1;
      while (end < items.length && items[end].dataset.variantAnchor !== "true") {
        end++;
      }

      return new Set(items.slice(start, end).map(mediaIdOf));
    }

    apply(targetMediaId) {
      this.lastTarget = targetMediaId;

      const allowed = this.rangeFor(targetMediaId);

      this.lists.forEach((list) => {
        const next = allowed
          ? list.items.filter((el) => allowed.has(mediaIdOf(el)))
          : list.items;

        if (!next.length) return;

        const current = Array.from(list.wrapper.children).filter(isGalleryItem);
        const unchanged =
          current.length === next.length &&
          current.every((el, index) => el === next[index]);

        if (unchanged) return;

        current.forEach((el) => el.remove());
        next.forEach((el) => list.wrapper.appendChild(el));

        const swiper = list.swiperEl && list.swiperEl.swiper;

        if (swiper) {
          swiper.update();
          swiper.slideTo(0, 0, false);
        } else {
          list.wrapper.scrollTo?.(0, 0);
        }
      });

      this.toggleNavigation(
        allowed ? allowed.size : this.reference ? this.reference.items.length : 0
      );
    }

    /**
     * The arrows and bullets are rendered for the full media set, so hide them
     * when the selected variant is down to a single photo.
     */
    toggleNavigation(visibleCount) {
      const display = visibleCount > 1 ? "" : "none";

      this.section
        .querySelectorAll(
          ".product__slider-nav, .product__pagination-wrapper, .product__media-sublist__slider-nav"
        )
        .forEach((el) => {
          el.style.display = display;
        });
    }

    /**
     * Media id of the variant selected right now. Prefers ?variant= in the URL,
     * then the variant the section was rendered with. Only when neither can be
     * read does it fall back to the first anchor; a variant that genuinely has
     * no media of its own must yield null, so the full gallery stays visible.
     */
    selectedMediaId() {
      const fromUrl = this.mediaIdFromUrl();

      if (fromUrl) return fromUrl;

      const data = this.section.querySelector("[data-selected-variant]");

      if (data) {
        try {
          const variant = JSON.parse(data.textContent);

          return variant && variant.featured_media
            ? `${this.sectionId}-${variant.featured_media.id}`
            : null;
        } catch (err) {
          console.warn(
            "variant-media-group: could not read the selected variant",
            err
          );
        }
      }

      const firstAnchor = this.reference
        ? this.reference.items.find((el) => el.dataset.variantAnchor === "true")
        : null;

      return firstAnchor ? mediaIdOf(firstAnchor) : null;
    }

    /** Media id for the variant named in ?variant= , if the URL carries one. */
    mediaIdFromUrl() {
      const variantId = new URLSearchParams(window.location.search).get(
        "variant"
      );

      if (!variantId) return null;

      const data = this.section.querySelector("[data-all-variants-no-high]");

      if (!data) return null;

      try {
        const variant = JSON.parse(data.textContent).find(
          (candidate) => String(candidate.id) === String(variantId)
        );

        return variant && variant.featured_media
          ? `${this.sectionId}-${variant.featured_media.id}`
          : null;
      } catch (err) {
        console.warn("variant-media-group: could not read the variant list", err);
        return null;
      }
    }

    /** Re-assert whatever range should be showing, without recomputing it. */
    refresh() {
      this.apply(
        this.lastTarget !== undefined ? this.lastTarget : this.selectedMediaId()
      );
    }
  }

  /**
   * The gallery must be filtered only once Swiper owns the slides, otherwise
   * Swiper initialises on the full list and ends up counting slides that are no
   * longer there. Waits for the main slider, then gives up after ~1s so a
   * layout without Swiper still gets filtered.
   */
  const whenSlidersReady = (section, callback, attempts = 60) => {
    const list = section.querySelector(".js-media-list");

    if (!list || list.swiper || attempts <= 0) {
      callback();
      return;
    }

    requestAnimationFrame(() => whenSlidersReady(section, callback, attempts - 1));
  };

  const initVariantMediaGroups = () => {
    galleries.length = 0;

    document
      .querySelectorAll(".product__media-wrapper[data-variant-media-group]")
      .forEach((wrapper) => {
        const section = wrapper.closest(".main-product");

        if (!section) return;

        // The wrapper carries data-section="product-media-<sectionId>", which is
        // a more reliable source for the id than the ancestor element.
        const sectionId = (wrapper.dataset.section || "").replace(
          /^product-media-/,
          ""
        );

        const gallery = new VariantMediaGroup(section, sectionId);
        galleries.push(gallery);

        whenSlidersReady(section, () =>
          gallery.apply(gallery.selectedMediaId())
        );
      });
  };

  /**
   * Called from VariantSelects.updateMedia() before it scrolls the sliders, so
   * the slide index it looks up is the one inside the filtered gallery.
   */
  const applyVariantMediaGroup = (variantSelects, targetMediaId) => {
    if (!variantSelects || typeof variantSelects.closest !== "function") {
      galleries.forEach((gallery) =>
        targetMediaId ? gallery.apply(targetMediaId) : gallery.refresh()
      );
      return;
    }

    // Match on the section id, not on DOM ancestry. A picker is tied to its
    // section by data-section; it is not necessarily still a descendant of the
    // section element by the time this runs, because updatePickerInnerHtml()
    // rewrites the picker markup on every variant change.
    const sectionId = variantSelects.dataset && variantSelects.dataset.section;

    let matches = sectionId
      ? galleries.filter((gallery) => gallery.sectionId === sectionId)
      : [];

    if (!matches.length) {
      const section = variantSelects.closest(".main-product");

      matches = section
        ? galleries.filter((gallery) => gallery.section === section)
        : [];
    }

    matches.forEach((gallery) => gallery.apply(targetMediaId));
  };

  window.initVariantMediaGroups = initVariantMediaGroups;
  window.applyVariantMediaGroup = applyVariantMediaGroup;

  document.addEventListener("shopify:section:load", initVariantMediaGroups);

  // Guard against anything that puts the full media list back late in the load:
  // the theme itself calls slider.update() 800ms after init. Re-asserting the
  // range is a no-op when the gallery is already correct.
  const refreshAll = () => galleries.forEach((gallery) => gallery.refresh());

  window.addEventListener("load", refreshAll);
  window.setTimeout(refreshAll, 1200);

  initVariantMediaGroups();
})();
